import React, {useCallback, useEffect} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import navigateToURL from 'lib/navigateToURL';
import {
  SingleMonomerQuery,
  SingleMonomerQueryVariables,
} from 'molbio/monomers/__generated/SingleMonomerQuery';
import MonomerConfigurationPageContainer, {
  MONOMER_CONFIGURATION_PURPOSE,
} from 'molbio/monomers/admin/MonomerConfigurationPageContainer';
import SharedMonomerManualEditPage from 'molbio/monomers/admin/SharedMonomerManualEditPage';
import {gqlToAPINaturalAnalogue} from 'molbio/monomers/MonomersUtil';
import notifications from 'notifications';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import legacyRunModal from 'ui-library/legacyRunModal';
import Modal from 'ui-library/modals/Modal';

import {SINGLE_MONOMER_QUERY} from '../MonomerConstants';
import {updateMonomer as updateMonomerHelper, validateMonomerInput} from '../MonomersDAO';
import {
  BaseMonomerUpdate,
  BaseMonomerValidationFields,
  SharedMonomerValidationOutput,
} from '../SharedMonomerConstants';

export interface MonomerManualEditPageProps {
  monomerId: string;
}

export function MonomerManualEditConfirmationModal(props: {
  onRequestClose: () => void;
  updateMonomer: () => void;
}): JSX.Element {
  return (
    <Modal
      onRequestClose={props.onRequestClose}
      buttons={[
        {
          style: ButtonStyle.SECONDARY,
          submit: true,
          onClick: () => {
            props.updateMonomer();
            props.onRequestClose();
          },
          text: 'Yes, save monomer',
        },
      ]}
      title="Edit monomer structure?"
    >
      <p>
        If this monomer is used in a complex polymer, editing the structure may invalidate registered complex
        polymers and remove existing conjugate attachments. Are you sure you want to continue editing the
        structure?
      </p>
    </Modal>
  );
}

const MONOMER_LIBRARY_URL = '/monomers';

export default function MonomerManualEditPage({monomerId}: MonomerManualEditPageProps): JSX.Element | null {
  const queryResult = useBenchlingQuery<SingleMonomerQuery, SingleMonomerQueryVariables>(
    SINGLE_MONOMER_QUERY,
    {
      variables: {id: monomerId},
      renderCachedDataWhenRefetching: true,
    }
  );

  const monomerNode = queryResult.type === QueryResultType.SUCCESS ? queryResult.data.monomer : null;
  const monomer = monomerNode?.__typename === 'MonomerNode' ? monomerNode : null;
  const isEditingNaturalMonomer = monomer?.isNatural ?? false;
  const shouldRedirect =
    queryResult.type === QueryResultType.ERROR ||
    (queryResult.type === QueryResultType.SUCCESS && monomer == null) ||
    isEditingNaturalMonomer;

  const naturalAnalogue = gqlToAPINaturalAnalogue(monomer?.naturalAnalogue);
  const validate = useCallback(
    (inputs: Array<BaseMonomerValidationFields>): Promise<Array<SharedMonomerValidationOutput>> =>
      validateMonomerInput(inputs.map((input) => ({...input, naturalAnalogue}))),
    [naturalAnalogue]
  );

  useEffect(() => {
    if (!shouldRedirect) {
      return;
    }
    if (isEditingNaturalMonomer) {
      notifications.info('Cannot edit a natural monomer');
    }
    navigateToURL(MONOMER_LIBRARY_URL);
  }, [shouldRedirect, isEditingNaturalMonomer]);

  if (queryResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  }

  if (monomer == null || shouldRedirect) {
    return null;
  }

  return (
    <SharedMonomerManualEditPage
      monomerId={monomerId}
      initialName={monomer.name}
      initialCustomMolecularWeight={monomer.customMolecularWeight}
      initialSmilesInput={monomer.originalSmiles}
      initialSymbol={monomer.symbol}
      validate={validate}
      onSave={async (update: BaseMonomerUpdate) => {
        await updateMonomerHelper(monomerId, update);
      }}
      onSaveRequested={({didStructureChange, save}) => {
        if (didStructureChange && BenchlingConfig.MODBIO_ENABLE_COMPOSITE_ENTITIES) {
          legacyRunModal(MonomerManualEditConfirmationModal, {updateMonomer: save});
        } else {
          save();
        }
      }}
      libraryUrl={MONOMER_LIBRARY_URL}
      pageContainer={<MonomerConfigurationPageContainer purpose={MONOMER_CONFIGURATION_PURPOSE.EDIT} />}
      dataTestComponent="MonomerManualEditPage"
    />
  );
}
